import React, { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    Divider,
    Grid,
    IconButton,
    makeStyles,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";
import { useHistory } from "react-router-dom";
import customersService from "../../../../services/customers.service";
import glassesService from "../../../../services/glasses.service";
import ordersService from "../../../../services/orders.service";
import lensesService from "../../../../services/lenses.service";
import brandsService from "../../../../services/brands.service";
import colorsService from "../../../../services/colors.service";
import { currencyFormat, phoneFormat } from "../../../../helpers";
import { MOBILE_REG } from "../../../../constants/regex";
import { useErrorHandler } from "../../../../hooks";
import LensFormModalContent from "../../lenses/form";
import CurrencyInput from "../../../../components/currency-input";
import QuickGlassModal from "./quick-glass-modal";
import DatePicker from "../../../../components/formik-material-ui/date-picker";
import PrescriptionModal from "../../users/prescription/prescription-modal";

const useStyles = makeStyles((theme) => ({
    section: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    rowCard: {
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    totalsBox: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        background: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
    },
    resultPaper: {
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    amountField: {
        "& input": {
            fontFamily: "monospace",
        },
    },
}));

function normalizePhone(phone = "") {
    const digits = `${phone}`.replace(/\D/g, "");
    if (digits.startsWith("98") && digits.length === 12) {
        return `0${digits.slice(2)}`;
    }
    if (digits.length === 10 && digits.startsWith("9")) {
        return `0${digits}`;
    }
    return digits;
}

function isValidPhone(phone) {
    const normalized = normalizePhone(phone);
    return new RegExp(MOBILE_REG.source).test(normalized);
}

function isProgressiveLens(lens) {
    return /تدریجی|progressive/i.test((lens?.name || "").replace(/ي/g, "ی"));
}

function createEmptyItem() {
    return {
        key: Math.random().toString(36).slice(2),
        searchText: "",
        searchLoading: false,
        searchResults: [],
        selectedGlass: null,
        selectedColorId: "",
        selectedSizeId: "",
        quantity: 1,
        withLens: false,
        selectedPrescriptionId: "",
        doctorName: "",
        rightEyeSph: "",
        rightEyeCyl: "",
        rightEyeAxe: "",
        leftEyeSph: "",
        leftEyeCyl: "",
        leftEyeAxe: "",
        pd: "",
        add: "",
        prescriptionDescription: "",
        selectedLensId: "",
        measurementFile: null,
        measurementImage: "",
        lensPrice: "",
        insuranceContribution: "",
    };
}

function createEmptyPrescriptionForm() {
    return {
        title: "",
        image: null,
        imageUrl: "",
        doctorName: "",
        rightEyeSph: "",
        rightEyeCyl: "",
        rightEyeAxe: "",
        leftEyeSph: "",
        leftEyeCyl: "",
        leftEyeAxe: "",
        pd: "",
        add: "",
        description: "",
    };
}

function createEmptyQuickGlassForm() {
    return {
        name: "",
        category: "",
        brandId: "",
        colorName: "",
        colorId: "",
        colorValue: "",
        price: "",
    };
}

function getSizeLabel(size) {
    if (!size) return "";
    if (size.name) return size.name;
    if (size.hingeToHinge > 139) return "بزرگ";
    if (size.hingeToHinge < 130) return "کوچک";
    return "متوسط";
}

function getItemColor(item) {
    return item.selectedGlass?.colors?.find((color) => color.id === item.selectedColorId);
}

function getItemSize(item) {
    return getItemColor(item)?.sizes?.find((size) => size.id === item.selectedSizeId);
}

function getItemUnitPrice(item) {
    return Number(getItemColor(item)?.price || 0);
}

function isEyeglass(item) {
    return item.selectedGlass?.category === "eyeglass";
}

function getSelectedLens(item, lenses) {
    return lenses.find((lens) => lens.id === item.selectedLensId);
}

function getItemLensPrice(item) {
    return item.withLens && isEyeglass(item) ? Number(item.lensPrice || 0) : 0;
}

function getItemLensInsurance(item) {
    return item.withLens && isEyeglass(item)
        ? Number(item.insuranceContribution || 0)
        : 0;
}

function getItemTotal(item) {
    return (
        getItemUnitPrice(item) +
        getItemLensPrice(item) -
        getItemLensInsurance(item)
    ) * Number(item.quantity || 0);
}

function getItemQuantityWarning(item) {
    const size = getItemSize(item);
    if (size?.count >= 0 && Number(item.quantity || 0) > Number(size.count || 0)) {
        return "تعداد واردشده از موجودی این سایز بیشتر است.";
    }
    return "";
}

export default function InPersonPurchasePage() {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const errorHandler = useErrorHandler();
    const { initials } = useSelector((state) => state.app);

    const [customerSearch, setCustomerSearch] = useState("");
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerMatches, setCustomerMatches] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [manualCustomer, setManualCustomer] = useState({
        phoneNumber: "",
        firstName: "",
        lastName: "",
    });
    const [depositAmount, setDepositAmount] = useState("");
    const [discountAmount, setDiscountAmount] = useState("");
    const [paymentProvider, setPaymentProvider] = useState("cash");
    const [description, setDescription] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });
    const [items, setItems] = useState([createEmptyItem()]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [lenses, setLenses] = useState([]);
    const [brands, setBrands] = useState([]);
    const [colors, setColors] = useState([]);
    const [prescriptions, setPrescriptions] = useState([]);
    const [prescriptionLoading, setPrescriptionLoading] = useState(false);
    const [prescriptionModal, setPrescriptionModal] = useState({
        open: false,
        itemKey: "",
        mode: "create",
        prescriptionId: "",
    });
    const [prescriptionForm, setPrescriptionForm] = useState(createEmptyPrescriptionForm());
    const [prescriptionSubmitLoading, setPrescriptionSubmitLoading] = useState(false);
    const [lensModal, setLensModal] = useState({
        open: false,
        itemKey: "",
    });
    const [quickGlassModal, setQuickGlassModal] = useState({
        open: false,
        itemKey: "",
    });
    const [quickGlassForm, setQuickGlassForm] = useState(createEmptyQuickGlassForm());
    const [quickGlassLoading, setQuickGlassLoading] = useState(false);

    useEffect(() => {
        const loadLenses = async () => {
            const { data, error } = await lensesService.getLenses();
            if (error) {
                errorHandler(error);
                return;
            }
            setLenses(data?.items || data || []);
        };
        loadLenses();
    }, []);

    useEffect(() => {
        const loadQuickGlassOptions = async () => {
            const [brandsRes, colorsRes] = await Promise.all([
                brandsService.getAllBrands(),
                colorsService.getColors(),
            ]);

            if (brandsRes.error) {
                errorHandler(brandsRes.error);
            } else {
                setBrands(brandsRes.data?.items || brandsRes.data || []);
            }

            if (colorsRes.error) {
                errorHandler(colorsRes.error);
            } else {
                setColors(colorsRes.data?.items || colorsRes.data || []);
            }
        };

        loadQuickGlassOptions();
    }, []);

    useEffect(() => {
        const loadPrescriptions = async () => {
            if (!selectedCustomer?.id) {
                setPrescriptions([]);
                return;
            }
            setPrescriptionLoading(true);
            const { data, error } = await customersService.getCustomerPrescriptions(
                selectedCustomer.id
            );
            setPrescriptionLoading(false);
            if (error) {
                errorHandler(error);
                return;
            }
            setPrescriptions(data?.items || data || []);
        };
        loadPrescriptions();
    }, [selectedCustomer?.id]);

    const totalAmount = useMemo(
        () => items.reduce((sum, item) => sum + getItemTotal(item), 0),
        [items]
    );
    const depositValue = Number(depositAmount || 0);
    const discountValue = Number(discountAmount || 0);
    const payableAmount = Math.max(totalAmount - discountValue, 0);
    const remainingAmount = Math.max(payableAmount - depositValue, 0);

    const handleCustomerSearch = async () => {
        if (!customerSearch.trim()) {
            enqueueSnackbar("شماره تلفن مشتری را وارد کنید.", { variant: "warning" });
            return;
        }

        setCustomerLoading(true);
        const { data, error } = await customersService.searchCustomers(customerSearch.trim(), {
            filterFields: ["phoneNumber", "fullName"],
        });
        setCustomerLoading(false);

        if (error) {
            errorHandler(error);
            return;
        }

        const matches = data?.items || data || [];
        setCustomerMatches(matches);

        const exactMatch = matches.find(
            (customer) =>
                normalizePhone(customer.phoneNumber) === normalizePhone(customerSearch)
        );

        if (exactMatch) {
            setSelectedCustomer(exactMatch);
            enqueueSnackbar("مشتری پیدا شد.", { variant: "success" });
            return;
        }

        setSelectedCustomer(null);
        setManualCustomer((prev) => ({
            ...prev,
            phoneNumber: normalizePhone(customerSearch),
        }));
        enqueueSnackbar("مشتری یافت نشد. اطلاعات را دستی کامل کنید.", {
            variant: "info",
        });
    };

    const updateItem = (key, updates) => {
        setItems((prev) =>
            prev.map((item) => (item.key === key ? { ...item, ...updates } : item))
        );
    };

    const handleGlassSearch = async (key) => {
        const item = items.find((entry) => entry.key === key);
        if (!item?.searchText?.trim()) {
            enqueueSnackbar("نام یا کد عینک را برای جستجو وارد کنید.", {
                variant: "warning",
            });
            return;
        }

        updateItem(key, { searchLoading: true });
        const { data, error } = await glassesService.searchGlasses(item.searchText.trim(), {
            includeInactive: true,
        });
        updateItem(key, {
            searchLoading: false,
            searchResults: data?.items || data || [],
        });

        if (error) {
            errorHandler(error);
            return;
        }
    };

    const handleGlassSelect = (key, glass) => {
        updateItem(key, {
            selectedGlass: glass,
            selectedColorId: "",
            selectedSizeId: "",
            searchResults: glass ? [glass] : [],
            withLens: false,
            selectedPrescriptionId: "",
            doctorName: "",
            rightEyeSph: "",
            rightEyeCyl: "",
            rightEyeAxe: "",
            leftEyeSph: "",
            leftEyeCyl: "",
            leftEyeAxe: "",
            pd: "",
            add: "",
            prescriptionDescription: "",
            selectedLensId: "",
            lensPrice: "",
            insuranceContribution: "",
        });
    };

    const openQuickGlassModal = (itemKey) => {
        setQuickGlassModal({
            open: true,
            itemKey,
        });
        setQuickGlassForm(createEmptyQuickGlassForm());
    };

    const closeQuickGlassModal = () => {
        if (quickGlassLoading) return;
        setQuickGlassModal({
            open: false,
            itemKey: "",
        });
        setQuickGlassForm(createEmptyQuickGlassForm());
    };

    const validateQuickGlassForm = () => {
        if (!quickGlassForm.name.trim()) {
            enqueueSnackbar("نام عینک را وارد کنید.", { variant: "warning" });
            return false;
        }
        if (!quickGlassForm.category) {
            enqueueSnackbar("نوع عینک را انتخاب کنید.", { variant: "warning" });
            return false;
        }
        if (!quickGlassForm.brandId) {
            enqueueSnackbar("برند عینک را انتخاب کنید.", { variant: "warning" });
            return false;
        }
        if (!quickGlassForm.colorName.trim()) {
            enqueueSnackbar("نام رنگ را وارد کنید.", { variant: "warning" });
            return false;
        }
        if (!quickGlassForm.colorId) {
            enqueueSnackbar("رنگ پایه را انتخاب کنید.", { variant: "warning" });
            return false;
        }
        if (Number(quickGlassForm.price || 0) < 1000) {
            enqueueSnackbar("قیمت رنگ باید حداقل ۱۰۰۰ تومان باشد.", {
                variant: "warning",
            });
            return false;
        }
        return true;
    };

    const handleCreateQuickGlass = async () => {
        if (!validateQuickGlassForm()) return;

        setQuickGlassLoading(true);

        const selectedBrand = brands.find((brand) => brand.id === quickGlassForm.brandId);
        const selectedBaseColor = colors.find((color) => color.id === quickGlassForm.colorId);

        const glassPayload = {
            name: quickGlassForm.name.trim(),
            category: quickGlassForm.category,
            brandId: quickGlassForm.brandId,
            summery: "",
            description: "",
            weight: 0,
            bridge: 0,
            gender: "unisex",
            material: "plastic",
            lensMaterial: "plastic",
            shape: "rectangle",
            faceTypes: ["round"],
            specialFeatures: [],
            isActive: false,
        };

        const { data: createdGlass, error: glassError } =
            await glassesService.createGlasses(glassPayload);
        if (glassError) {
            setQuickGlassLoading(false);
            errorHandler(glassError);
            return;
        }

        const colorPayload = {
            glassesId: createdGlass.id,
            name: quickGlassForm.colorName.trim(),
            colorId: quickGlassForm.colorId,
            colorValue: quickGlassForm.colorValue || selectedBaseColor?.color || "",
            price: Number(quickGlassForm.price || 0),
            discount: 0,
            isActive: true,
            isMain: true,
            fixCost: false,
            fixDiscount: false,
        };

        const { data: createdColor, error: colorError } =
            await glassesService.createGlassesColor(colorPayload);
        if (colorError) {
            setQuickGlassLoading(false);
            errorHandler(colorError);
            return;
        }

        const sizePayload = {
            glassesColorId: createdColor.id,
            count: 1,
            lensSize: 0,
            bridgeSize: 0,
            templeLength: 0,
            hingeToHinge: 130,
            lensHeight: 0,
        };

        const { data: createdSize, error: sizeError } =
            await glassesService.createGlassesSize(sizePayload);
        setQuickGlassLoading(false);

        if (sizeError) {
            errorHandler(sizeError);
            return;
        }

        const createdColorWithSize = {
            ...createdColor,
            color: selectedBaseColor || createdColor.color,
            sizes: [createdSize],
        };
        const createdGlassForOrder = {
            ...createdGlass,
            brand: selectedBrand || createdGlass.brand,
            colors: [createdColorWithSize],
        };

        updateItem(quickGlassModal.itemKey, {
            searchText: createdGlassForOrder.name,
            searchResults: [createdGlassForOrder],
            selectedGlass: createdGlassForOrder,
            selectedColorId: createdColorWithSize.id,
            selectedSizeId: createdSize.id,
            quantity: 1,
            withLens: false,
            selectedPrescriptionId: "",
            doctorName: "",
            rightEyeSph: "",
            rightEyeCyl: "",
            rightEyeAxe: "",
            leftEyeSph: "",
            leftEyeCyl: "",
            leftEyeAxe: "",
            pd: "",
            add: "",
            prescriptionDescription: "",
            selectedLensId: "",
            lensPrice: "",
            insuranceContribution: "",
        });
        enqueueSnackbar("عینک جدید ساخته و انتخاب شد.", { variant: "success" });
        closeQuickGlassModal();
    };

    const handleColorSelect = (key, colorId) => {
        updateItem(key, {
            selectedColorId: colorId,
            selectedSizeId: "",
        });
    };

    const addItem = () => {
        setItems((prev) => [...prev, createEmptyItem()]);
    };

    const removeItem = (key) => {
        setItems((prev) => {
            if (prev.length === 1) return prev;
            return prev.filter((item) => item.key !== key);
        });
    };

    const selectedCustomerName = useMemo(() => {
        if (selectedCustomer) return selectedCustomer.fullName;
        return `${manualCustomer.firstName} ${manualCustomer.lastName}`.trim();
    }, [manualCustomer.firstName, manualCustomer.lastName, selectedCustomer]);

    const openPrescriptionModal = (item) => {
        setPrescriptionModal({
            open: true,
            itemKey: item.key,
            mode: "create",
            prescriptionId: "",
        });
        setPrescriptionForm({
            ...createEmptyPrescriptionForm(),
            title: item.selectedGlass?.name || "",
            doctorName: item.doctorName || "",
            rightEyeSph: item.rightEyeSph || "",
            rightEyeCyl: item.rightEyeCyl || "",
            rightEyeAxe: item.rightEyeAxe || "",
            leftEyeSph: item.leftEyeSph || "",
            leftEyeCyl: item.leftEyeCyl || "",
            leftEyeAxe: item.leftEyeAxe || "",
            pd: item.pd || "",
            add: item.add || "",
            description: item.prescriptionDescription || "",
        });
    };

    const openPrescriptionEditModal = (item) => {
        if (!item.selectedPrescriptionId) {
            enqueueSnackbar("ابتدا نسخه را انتخاب کنید.", { variant: "warning" });
            return;
        }

        const prescription = prescriptions.find(
            (entry) => entry.id === item.selectedPrescriptionId
        );

        if (!prescription) {
            enqueueSnackbar("نسخه انتخاب‌شده پیدا نشد.", { variant: "warning" });
            return;
        }

        setPrescriptionModal({
            open: true,
            itemKey: item.key,
            mode: "edit",
            prescriptionId: prescription.id,
        });
        setPrescriptionForm({
            ...createEmptyPrescriptionForm(),
            title: prescription.title || "",
            imageUrl: prescription.image || "",
            doctorName: prescription.doctorName || "",
            rightEyeSph: prescription.rightEyeSph || "",
            rightEyeCyl: prescription.rightEyeCyl || "",
            rightEyeAxe: prescription.rightEyeAxe || "",
            leftEyeSph: prescription.leftEyeSph || "",
            leftEyeCyl: prescription.leftEyeCyl || "",
            leftEyeAxe: prescription.leftEyeAxe || "",
            pd: prescription.pd || "",
            add: prescription.add || "",
            description: prescription.description || "",
        });
    };

    const closePrescriptionModal = () => {
        if (prescriptionSubmitLoading) return;
        setPrescriptionModal({
            open: false,
            itemKey: "",
            mode: "create",
            prescriptionId: "",
        });
        setPrescriptionForm(createEmptyPrescriptionForm());
    };

    const ensureCustomerForPrescription = async () => {
        if (selectedCustomer?.id) return selectedCustomer;

        if (!manualCustomer.firstName.trim() || !manualCustomer.lastName.trim()) {
            enqueueSnackbar("برای ثبت نسخه، نام و نام خانوادگی مشتری را کامل کنید.", {
                variant: "warning",
            });
            return null;
        }
        if (!isValidPhone(manualCustomer.phoneNumber)) {
            enqueueSnackbar("برای ثبت نسخه، شماره تلفن مشتری معتبر نیست.", {
                variant: "warning",
            });
            return null;
        }

        const { data, error } = await customersService.createCustomer({
            firstName: manualCustomer.firstName.trim(),
            lastName: manualCustomer.lastName.trim(),
            fullName: selectedCustomerName,
            phoneNumber: normalizePhone(manualCustomer.phoneNumber),
        });

        if (error) {
            errorHandler(error);
            return null;
        }

        setSelectedCustomer(data);
        return data;
    };

    const applyPrescriptionToItem = (prescription) => {
        updateItem(prescriptionModal.itemKey, {
            selectedPrescriptionId: prescription.id,
            doctorName: prescription.doctorName || "",
            rightEyeSph: prescription.rightEyeSph || "",
            rightEyeCyl: prescription.rightEyeCyl || "",
            rightEyeAxe: prescription.rightEyeAxe || "",
            leftEyeSph: prescription.leftEyeSph || "",
            leftEyeCyl: prescription.leftEyeCyl || "",
            leftEyeAxe: prescription.leftEyeAxe || "",
            pd: prescription.pd || "",
            add: prescription.add || "",
            prescriptionDescription: prescription.description || "",
        });
    };

    const normalizePrescriptionResponse = (data) => {
        const prescriptionResponse = data?.item || data;
        return {
            ...prescriptionResponse,
            title: prescriptionResponse?.title || prescriptionForm.title,
            image: prescriptionResponse?.image || prescriptionForm.imageUrl,
            doctorName: prescriptionResponse?.doctorName || prescriptionForm.doctorName,
            rightEyeSph: prescriptionResponse?.rightEyeSph || prescriptionForm.rightEyeSph,
            rightEyeCyl: prescriptionResponse?.rightEyeCyl || prescriptionForm.rightEyeCyl,
            rightEyeAxe: prescriptionResponse?.rightEyeAxe || prescriptionForm.rightEyeAxe,
            leftEyeSph: prescriptionResponse?.leftEyeSph || prescriptionForm.leftEyeSph,
            leftEyeCyl: prescriptionResponse?.leftEyeCyl || prescriptionForm.leftEyeCyl,
            leftEyeAxe: prescriptionResponse?.leftEyeAxe || prescriptionForm.leftEyeAxe,
            pd: prescriptionResponse?.pd || prescriptionForm.pd,
            add: prescriptionResponse?.add || prescriptionForm.add,
            description: prescriptionResponse?.description || prescriptionForm.description,
        };
    };

    const handleSubmitPrescription = async () => {
        const isEditMode = prescriptionModal.mode === "edit";
        if (!isEditMode && !prescriptionForm.image) {
            enqueueSnackbar("فایل نسخه را انتخاب کنید.", { variant: "warning" });
            return;
        }

        setPrescriptionSubmitLoading(true);
        const customerForPrescription = await ensureCustomerForPrescription();
        if (!customerForPrescription?.id) {
            setPrescriptionSubmitLoading(false);
            return;
        }

        const prescriptionPayload = {
            title: prescriptionForm.title,
            doctorName: prescriptionForm.doctorName,
            rightEyeSph: prescriptionForm.rightEyeSph,
            rightEyeCyl: prescriptionForm.rightEyeCyl,
            rightEyeAxe: prescriptionForm.rightEyeAxe,
            leftEyeSph: prescriptionForm.leftEyeSph,
            leftEyeCyl: prescriptionForm.leftEyeCyl,
            leftEyeAxe: prescriptionForm.leftEyeAxe,
            pd: prescriptionForm.pd,
            add: prescriptionForm.add,
            description: prescriptionForm.description,
        };
        if (prescriptionForm.image) {
            prescriptionPayload.image = prescriptionForm.image;
        }

        const { data, error } = isEditMode
            ? await customersService.updateCustomerPrescription(
                customerForPrescription.id,
                prescriptionModal.prescriptionId,
                prescriptionPayload
            )
            : await customersService.uploadCustomerPrescription(
                customerForPrescription.id,
                prescriptionPayload
            );
        setPrescriptionSubmitLoading(false);

        if (error) {
            errorHandler(error);
            return;
        }

        const savedPrescription = normalizePrescriptionResponse(data);

        if (!savedPrescription.id) {
            enqueueSnackbar("نسخه ذخیره شد، اما شناسه نسخه از سرور دریافت نشد.", {
                variant: "warning",
            });
            return;
        }

        setPrescriptions((prev) => [
            savedPrescription,
            ...prev.filter((item) => item.id !== savedPrescription.id),
        ]);
        applyPrescriptionToItem(savedPrescription);
        enqueueSnackbar(isEditMode ? "نسخه ویرایش شد." : "نسخه جدید ثبت شد.", {
            variant: "success",
        });
        closePrescriptionModal();
    };

    const validateBeforeSubmit = () => {
        if (!selectedCustomer) {
            if (!manualCustomer.firstName.trim() || !manualCustomer.lastName.trim()) {
                enqueueSnackbar("نام و نام خانوادگی مشتری را کامل کنید.", {
                    variant: "warning",
                });
                return false;
            }
            if (!isValidPhone(manualCustomer.phoneNumber)) {
                enqueueSnackbar("شماره تلفن مشتری معتبر نیست.", {
                    variant: "warning",
                });
                return false;
            }
        }

        if (!items.length) {
            enqueueSnackbar("حداقل یک آیتم خرید لازم است.", { variant: "warning" });
            return false;
        }

        let hasInventoryWarning = false;
        for (const item of items) {
            if (!item.selectedGlass) {
                enqueueSnackbar("برای همه ردیف‌ها عینک انتخاب کنید.", {
                    variant: "warning",
                });
                return false;
            }
            if (!item.selectedColorId) {
                enqueueSnackbar("برای همه ردیف‌ها رنگ را مشخص کنید.", {
                    variant: "warning",
                });
                return false;
            }
            if (!item.selectedSizeId) {
                enqueueSnackbar("برای همه ردیف‌ها سایز را مشخص کنید.", {
                    variant: "warning",
                });
                return false;
            }
            if (Number(item.quantity || 0) < 1) {
                enqueueSnackbar("تعداد هر آیتم باید حداقل ۱ باشد.", {
                    variant: "warning",
                });
                return false;
            }

            if (isEyeglass(item) && item.withLens) {
                if (!item.selectedLensId) {
                    enqueueSnackbar("برای آیتم‌های با عدسی، عدسی را انتخاب کنید.", {
                        variant: "warning",
                    });
                    return false;
                }
                if (item.lensPrice === "" || Number(item.lensPrice || 0) < 0) {
                    enqueueSnackbar("قیمت عدسی را به درستی وارد کنید.", {
                        variant: "warning",
                    });
                    return false;
                }
                if (
                    item.insuranceContribution === "" ||
                    Number(item.insuranceContribution || 0) < 0
                ) {
                    enqueueSnackbar("سهم بیمه عدسی را به درستی وارد کنید.", {
                        variant: "warning",
                    });
                    return false;
                }
                if (Number(item.insuranceContribution || 0) > Number(item.lensPrice || 0)) {
                    enqueueSnackbar("سهم بیمه نمی‌تواند از قیمت عدسی بیشتر باشد.", {
                        variant: "warning",
                    });
                    return false;
                }
                if (!item.selectedPrescriptionId) {
                    enqueueSnackbar("نسخه را انتخاب کنید یا نسخه جدید بسازید.", {
                        variant: "warning",
                    });
                    return false;
                }
            }

            const size = getItemSize(item);
            if (size?.count >= 0 && Number(item.quantity || 0) > Number(size.count || 0)) {
                hasInventoryWarning = true;
            }

            //   const size = getItemSize(item);
            //   if (size?.count >= 0 && Number(item.quantity || 0) > Number(size.count || 0)) {
            //     enqueueSnackbar("تعداد انتخاب‌شده از موجودی سایز بیشتر است.", {
            //       variant: "warning",
            //     });
            //     return false;
            //   }
        }

        if (hasInventoryWarning) {
            enqueueSnackbar("تعداد انتخاب‌شده از موجودی سایت بیشتر است؛ در صورت موجودی فروشگاهی می‌توانید ثبت کنید.", {
                variant: "warning",
            });
        }

        if (depositValue < 0) {
            enqueueSnackbar("مبلغ بیعانه نمی‌تواند منفی باشد.", {
                variant: "warning",
            });
            return false;
        }

        if (discountValue < 0) {
            enqueueSnackbar("مبلغ تخفیف نمی‌تواند منفی باشد.", {
                variant: "warning",
            });
            return false;
        }

        if (discountValue > totalAmount) {
            enqueueSnackbar("مبلغ تخفیف نمی‌تواند بیشتر از مبلغ کل باشد.", {
                variant: "warning",
            });
            return false;
        }

        if (depositValue > payableAmount) {
            enqueueSnackbar("مبلغ بیعانه نمی‌تواند بیشتر از مبلغ قابل پرداخت باشد.", {
                variant: "warning",
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateBeforeSubmit()) return;

        setSubmitLoading(true);
        const measurementImages = new Map();
        for (const item of items) {
            if (!item.withLens || !isEyeglass(item) || !isProgressiveLens(getSelectedLens(item, lenses))) continue;
            let image = item.measurementImage;
            if (item.measurementFile) {
                const { data, error } = await ordersService.uploadMeasurement(item.measurementFile);
                if (error || !data?.image) {
                    setSubmitLoading(false);
                    if (error) errorHandler(error);
                    else enqueueSnackbar("بارگذاری عکس اندازه‌گیری ناموفق بود.", { variant: "error" });
                    return;
                }
                image = data.image;
                updateItem(item.key, { measurementImage: image, measurementFile: null });
            }
            if (image) measurementImages.set(item.key, image);
        }

        const payload = {
            customer: selectedCustomer
                ? {
                    id: selectedCustomer.id,
                }
                : {
                    firstName: manualCustomer.firstName.trim(),
                    lastName: manualCustomer.lastName.trim(),
                    fullName: selectedCustomerName,
                    phoneNumber: normalizePhone(manualCustomer.phoneNumber),
                },
            items: items.map((item) => {
                const color = getItemColor(item);
                const size = getItemSize(item);
                const withLens = isEyeglass(item) && item.withLens;
                const orderItem = {
                    glassId: item.selectedGlass.id,
                    colorId: color?.id,
                    glassesSizeId: size?.id,
                    quantity: Number(item.quantity || 0),
                };
                if (withLens) {
                    orderItem.withLens = true;
                    orderItem.lens = {
                        prescriptionId: item.selectedPrescriptionId,
                        lensId: item.selectedLensId,
                        measurementImage: measurementImages.get(item.key),
                        price: Number(item.lensPrice || 0),
                        insuranceContribution: Number(item.insuranceContribution || 0),
                        doctorName: item.doctorName,
                        rightEyeSph: item.rightEyeSph,
                        rightEyeCyl: item.rightEyeCyl,
                        rightEyeAxe: item.rightEyeAxe,
                        leftEyeSph: item.leftEyeSph,
                        leftEyeCyl: item.leftEyeCyl,
                        leftEyeAxe: item.leftEyeAxe,
                        pd: item.pd,
                        add: item.add,
                        prescriptionDescription: item.prescriptionDescription,
                    };
                }
                return orderItem;
            }),
            // Send the selected calendar day without shifting it to the previous UTC day.
            deliveryDate: deliveryDate
                ? `${deliveryDate.getFullYear()}-${String(deliveryDate.getMonth() + 1).padStart(2, "0")}-${String(deliveryDate.getDate()).padStart(2, "0")}`
                : undefined,
            depositAmount: depositValue,
            discountAmount: discountValue,
            paymentProvider,
            payments:
                depositValue > 0
                    ? [
                        {
                            amount: depositValue,
                            method: "inPerson",
                            paymentProvider,
                            description: "deposit",
                        },
                    ]
                    : [],
            description,
        };

        const { data, error } = await ordersService.createInPersonOrder(payload);
        setSubmitLoading(false);

        if (error) {
            errorHandler(error);
            return;
        }

        console.log("in-person-purchase", data);
        enqueueSnackbar("خرید حضوری ثبت شد.", {
            variant: "success",
        });
        history.push("/admin/in-person-orders");
    };

    const handlePrescriptionSelect = (key, prescriptionId) => {
        const prescription = prescriptions.find((item) => item.id === prescriptionId);
        updateItem(key, {
            selectedPrescriptionId: prescriptionId,
            doctorName: prescription?.doctorName || "",
            rightEyeSph: prescription?.rightEyeSph || "",
            rightEyeCyl: prescription?.rightEyeCyl || "",
            rightEyeAxe: prescription?.rightEyeAxe || "",
            leftEyeSph: prescription?.leftEyeSph || "",
            leftEyeCyl: prescription?.leftEyeCyl || "",
            leftEyeAxe: prescription?.leftEyeAxe || "",
            pd: prescription?.pd || "",
            add: prescription?.add || "",
            prescriptionDescription: prescription?.description || "",
        });
    };

    const handleLensSelect = (key, lensId) => {
        updateItem(key, {
            selectedLensId: lensId,
            measurementFile: null,
            measurementImage: "",
            lensPrice: lensId ? "" : "",
            insuranceContribution: "",
        });
    };

    const openLensModal = (itemKey) => {
        setLensModal({
            open: true,
            itemKey,
        });
    };

    const closeLensModal = async (savedLens) => {
        const targetItemKey = lensModal.itemKey;
        setLensModal({
            open: false,
            itemKey: "",
        });

        if (!savedLens) return;

        const createdLens = savedLens?.lens || savedLens;
        if (createdLens?.id) {
            setLenses((prev) => [
                createdLens,
                ...prev.filter((lens) => lens.id !== createdLens.id),
            ]);
            handleLensSelect(targetItemKey, createdLens.id);
            return;
        }

        const { data, error } = await lensesService.getLenses();
        if (error) {
            errorHandler(error);
            return;
        }
        const refreshedLenses = data?.items || data || [];
        setLenses(refreshedLenses);

        const matchedLens = refreshedLenses.find(
            (lens) => lens.name?.trim() === createdLens?.name?.trim()
        );
        if (matchedLens?.id) {
            handleLensSelect(targetItemKey, matchedLens.id);
        }
    };

    return (
        <div className="p-3">
            <Paper className={classes.section}>
                <Typography variant="h5">خرید حضوری</Typography>
            </Paper>

            <Paper className={classes.section}>
                <Typography variant="h6">اطلاعات مشتری</Typography>
                <Divider className="my-2" />

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="شماره تلفن مشتری"
                            value={customerSearch}
                            onChange={(event) => setCustomerSearch(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            color="primary"
                            variant="contained"
                            onClick={handleCustomerSearch}
                            startIcon={
                                customerLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />
                            }
                            disabled={customerLoading}
                        >
                            جستجو
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MuiAutocomplete
                            options={customerMatches}
                            value={selectedCustomer}
                            onChange={(event, value) => setSelectedCustomer(value)}
                            getOptionSelected={(option, value) => option.id === value.id}
                            getOptionLabel={(option) =>
                                option ? `${option.fullName || "-"} - ${phoneFormat(option.phoneNumber || "")}` : ""
                            }
                            noOptionsText="مشتری‌ای برای انتخاب وجود ندارد."
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    label="انتخاب از نتایج جستجو"
                                />
                            )}
                        />
                    </Grid>

                    {selectedCustomer ? (
                        <Grid item xs={12}>
                            <div className={classes.resultPaper}>
                                <Typography variant="subtitle1">مشتری انتخاب‌شده</Typography>
                                <Divider className="my-2" />
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Typography>نام: {selectedCustomer.fullName || "-"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography className="en">
                                            شماره تماس: {phoneFormat(selectedCustomer.phoneNumber || "-")}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography>کد ملی: {selectedCustomer.nationalCode || "-"}</Typography>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="نام"
                                    value={manualCustomer.firstName}
                                    onChange={(event) =>
                                        setManualCustomer((prev) => ({
                                            ...prev,
                                            firstName: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="نام خانوادگی"
                                    value={manualCustomer.lastName}
                                    onChange={(event) =>
                                        setManualCustomer((prev) => ({
                                            ...prev,
                                            lastName: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="شماره تلفن"
                                    value={manualCustomer.phoneNumber}
                                    onChange={(event) =>
                                        setManualCustomer((prev) => ({
                                            ...prev,
                                            phoneNumber: event.target.value,
                                        }))
                                    }
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>

            <Paper className={classes.section}>
                <Grid container justify="space-between" alignItems="center">
                    <Grid item>
                        <Typography variant="h6">لیست خرید</Typography>
                    </Grid>
                    <Grid item>
                        <Button
                            color="primary"
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={addItem}
                        >
                            افزودن آیتم
                        </Button>
                    </Grid>
                </Grid>
                <Divider className="my-2" />

                <Grid container spacing={2}>
                    {items.map((item, index) => {
                        const selectedColor = getItemColor(item);
                        const selectedSize = getItemSize(item);
                        const selectedLens = getSelectedLens(item, lenses);
                        const showLensFields = isEyeglass(item) && item.withLens;
                        const sizeOptions = selectedColor?.sizes || [];
                        const categoryTitle =
                            initials?.enums?.glassCategory?.find(
                                (entry) => entry.value === item.selectedGlass?.category
                            )?.title || "-";

                        return (
                            <Grid item xs={12} key={item.key}>
                                <div className={classes.rowCard}>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item xs={12}>
                                            <Grid container justify="space-between" alignItems="center">
                                                <Grid item>
                                                    <Typography variant="subtitle1">آیتم {index + 1}</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <IconButton
                                                        onClick={() => removeItem(item.key)}
                                                        disabled={items.length === 1}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="جستجوی عینک"
                                                value={item.searchText}
                                                onChange={(event) =>
                                                    updateItem(item.key, { searchText: event.target.value })
                                                }
                                                helperText="نام، برند یا کد را جستجو کنید."
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Button
                                                fullWidth
                                                color="primary"
                                                variant="contained"
                                                onClick={() => handleGlassSearch(item.key)}
                                                startIcon={
                                                    item.searchLoading ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                    ) : (
                                                        <SearchIcon />
                                                    )
                                                }
                                                disabled={item.searchLoading}
                                            >
                                                جستجو
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Button
                                                fullWidth
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => openQuickGlassModal(item.key)}
                                                startIcon={<AddCircleOutlineIcon />}
                                            >
                                                عینک جدید
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <MuiAutocomplete
                                                options={item.searchResults}
                                                value={item.selectedGlass}
                                                onChange={(event, value) => handleGlassSelect(item.key, value)}
                                                getOptionSelected={(option, value) => option.id === value.id}
                                                getOptionLabel={(option) =>
                                                    option
                                                        ? `${option.name || "-"}${option.brand?.name ? ` - ${option.brand.name}` : ""}`
                                                        : ""
                                                }
                                                noOptionsText="نتیجه‌ای پیدا نشد."
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        label="انتخاب عینک"
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="نوع"
                                                value={categoryTitle}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <MuiAutocomplete
                                                options={item.selectedGlass?.colors || []}
                                                value={selectedColor || null}
                                                onChange={(event, value) =>
                                                    handleColorSelect(item.key, value?.id || "")
                                                }
                                                getOptionSelected={(option, value) => option.id === value.id}
                                                getOptionLabel={(option) => option?.name || ""}
                                                noOptionsText="رنگی برای این عینک ثبت نشده."
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        label="رنگ"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                variant="outlined"
                                                label="سایز"
                                                value={item.selectedSizeId}
                                                onChange={(event) =>
                                                    updateItem(item.key, { selectedSizeId: event.target.value })
                                                }
                                                disabled={!selectedColor}
                                            >
                                                {sizeOptions.map((size) => (
                                                    <MenuItem key={size.id} value={size.id}>
                                                        {getSizeLabel(size)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                variant="outlined"
                                                label="تعداد"
                                                value={item.quantity}
                                                onChange={(event) =>
                                                    updateItem(item.key, {
                                                        quantity: event.target.value,
                                                    })
                                                }
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                        {isEyeglass(item) && (
                                            <Grid item xs={12} md={2}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    variant="outlined"
                                                    label="عدسی"
                                                    value={item.withLens ? "yes" : "no"}
                                                    onChange={(event) =>
                                                        updateItem(item.key, {
                                                            withLens: event.target.value === "yes",
                                                            selectedPrescriptionId: "",
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="no">بدون عدسی</MenuItem>
                                                    <MenuItem value="yes">با عدسی</MenuItem>
                                                </TextField>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                label="قیمت واحد"
                                                value={currencyFormat(getItemUnitPrice(item))}
                                                disabled
                                                className={classes.amountField}
                                            />
                                        </Grid>

                                        {showLensFields && (
                                            <>
                                                <Grid item xs={12} md={5}>
                                                    <MuiAutocomplete
                                                        options={prescriptions}
                                                        loading={prescriptionLoading}
                                                        value={
                                                            prescriptions.find(
                                                                (entry) => entry.id === item.selectedPrescriptionId
                                                            ) || null
                                                        }
                                                        onChange={(event, value) =>
                                                            handlePrescriptionSelect(item.key, value?.id || "")
                                                        }
                                                        getOptionSelected={(option, value) => option.id === value.id}
                                                        getOptionLabel={(option) =>
                                                            option
                                                                ? `${option.title || "نسخه"} - ${option.doctorName || "-"
                                                                }`
                                                                : ""
                                                        }
                                                        noOptionsText="نسخه‌ای برای این مشتری ثبت نشده."
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                fullWidth
                                                                variant="outlined"
                                                                label="انتخاب نسخه"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                {isProgressiveLens(selectedLens) && (
                                                    <Grid item xs={12} md={5}>
                                                        <Button component="label" variant="outlined" color="primary" disabled={submitLoading}>
                                                            عکس اندازه‌گیری (اختیاری)
                                                            <input type="file" hidden accept="image/png,image/jpeg,image/gif,image/webp"
                                                                onChange={(event) => {
                                                                    const file = event.target.files?.[0];
                                                                    event.target.value = "";
                                                                    if (!file) return;
                                                                    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type) || file.size > 10 * 1024 * 1024) {
                                                                        enqueueSnackbar("یک عکس با حجم حداکثر ۱۰ مگابایت انتخاب کنید.", { variant: "error" });
                                                                        return;
                                                                    }
                                                                    updateItem(item.key, { measurementFile: file, measurementImage: "" });
                                                                }}
                                                            />
                                                        </Button>
                                                        <Typography variant="caption" display="block">{item.measurementFile?.name || (item.measurementImage ? "عکس بارگذاری شده است." : "جدا از فایل نسخه")}</Typography>
                                                        {(item.measurementFile || item.measurementImage) && (
                                                            <Button size="small" disabled={submitLoading} onClick={() => updateItem(item.key, { measurementFile: null, measurementImage: "" })}>حذف عکس اندازه‌گیری</Button>
                                                        )}
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} md={2}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => openPrescriptionModal(item)}
                                                        startIcon={<AddCircleOutlineIcon />}
                                                    >
                                                        نسخه جدید
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => openPrescriptionEditModal(item)}
                                                        disabled={!item.selectedPrescriptionId}
                                                    >
                                                        مشاهده / ویرایش
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <MuiAutocomplete
                                                        options={lenses}
                                                        value={selectedLens || null}
                                                        onChange={(event, value) =>
                                                            handleLensSelect(item.key, value?.id || "")
                                                        }
                                                        getOptionSelected={(option, value) => option.id === value.id}
                                                        getOptionLabel={(option) => option?.name || ""}
                                                        noOptionsText="عدسی‌ای ثبت نشده."
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                fullWidth
                                                                variant="outlined"
                                                                label="عدسی"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => openLensModal(item.key)}
                                                        startIcon={<AddCircleOutlineIcon />}
                                                    >
                                                        عدسی جدید
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        label="قیمت عدسی"
                                                        value={item.lensPrice}
                                                        InputProps={{ inputComponent: CurrencyInput }}
                                                        onChange={(event) =>
                                                            updateItem(item.key, { lensPrice: event.target.value })
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        label="سهم بیمه"
                                                        value={item.insuranceContribution}
                                                        InputProps={{ inputComponent: CurrencyInput }}
                                                        onChange={(event) =>
                                                            updateItem(item.key, {
                                                                insuranceContribution: event.target.value,
                                                            })
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        <Grid item xs={12}>
                                            <Grid container spacing={1}>
                                                {selectedColor && (
                                                    <Grid item>
                                                        <Chip label={`رنگ: ${selectedColor.name}`} />
                                                    </Grid>
                                                )}
                                                {selectedSize && (
                                                    <Grid item>
                                                        <Chip
                                                            label={`سایز: ${getSizeLabel(selectedSize)} | موجودی: ${selectedSize.count ?? "-"
                                                                }`}
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item>
                                                    <Chip
                                                        color="primary"
                                                        label={`جمع ردیف: ${currencyFormat(getItemTotal(item))}`}
                                                    />
                                                </Grid>
                                                {showLensFields && selectedLens && (
                                                    <Grid item>
                                                        <Chip
                                                            label={`عدسی: ${selectedLens.name} | ${currencyFormat(
                                                                getItemLensPrice(item) - getItemLensInsurance(item)
                                                            )}`}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>

            <Paper className={classes.section}>
                <Typography variant="h6">مبالغ و توضیحات</Typography>
                <Divider className="my-2" />

                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مبلغ کل"
                            value={currencyFormat(totalAmount)}
                            disabled
                            className={classes.amountField}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مبلغ تخفیف"
                            value={discountAmount}
                            onChange={(event) => setDiscountAmount(event.target.value)}
                            InputProps={{ inputComponent: CurrencyInput }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مبلغ قابل پرداخت"
                            value={currencyFormat(payableAmount)}
                            disabled
                            className={classes.amountField}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مبلغ بیعانه"
                            value={depositAmount}
                            onChange={(event) => setDepositAmount(event.target.value)}
                            InputProps={{ inputComponent: CurrencyInput }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="نوع پرداخت"
                            value={paymentProvider}
                            onChange={(event) => setPaymentProvider(event.target.value)}
                        >
                            <MenuItem value="cash">نقدی</MenuItem>
                            <MenuItem value="cardToCard">کارت به کارت</MenuItem>
                            <MenuItem value="snappPay">اسنپ پی</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مانده"
                            value={currencyFormat(remainingAmount)}
                            disabled
                            className={classes.amountField}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DatePicker
                            label="تاریخ تحویل (اختیاری)"
                            field={{ name: "deliveryDate", value: deliveryDate }}
                            form={{ setFieldValue: (_, value) => setDeliveryDate(value) }}
                            clearable
                            disabled={submitLoading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="توضیحات"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.totalsBox}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>نام عینک</TableCell>
                                        <TableCell>رنگ</TableCell>
                                        <TableCell>سایز</TableCell>
                                        <TableCell align="center">تعداد</TableCell>
                                        <TableCell align="right">قیمت واحد</TableCell>
                                        <TableCell align="right">جمع</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => {
                                        const color = getItemColor(item);
                                        const size = getItemSize(item);
                                        const selectedLens = getSelectedLens(item, lenses);
                                        const showLensFields = isEyeglass(item) && item.withLens;
                                        return (
                                            <React.Fragment key={item.key}>
                                                <TableRow>
                                                    <TableCell>{item.selectedGlass?.name || "-"}</TableCell>
                                                    <TableCell>{color?.name || "-"}</TableCell>
                                                    <TableCell>{size ? getSizeLabel(size) : "-"}</TableCell>
                                                    <TableCell align="center">{item.quantity || 0}</TableCell>
                                                    <TableCell align="right">
                                                        {currencyFormat(getItemUnitPrice(item))}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {currencyFormat(getItemUnitPrice(item) * Number(item.quantity || 0))}
                                                    </TableCell>
                                                </TableRow>
                                                {showLensFields && selectedLens && (
                                                    <TableRow>
                                                        <TableCell>{`عدسی ${selectedLens.name}`}</TableCell>
                                                        <TableCell colSpan={2}>-</TableCell>
                                                        <TableCell align="center">{item.quantity || 0}</TableCell>
                                                        <TableCell align="right">
                                                            {currencyFormat(
                                                                getItemLensPrice(item) - getItemLensInsurance(item)
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {currencyFormat(
                                                                (getItemLensPrice(item) - getItemLensInsurance(item)) *
                                                                Number(item.quantity || 0)
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitLoading}
                            startIcon={submitLoading ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            ثبت پیش‌نویس فرم خرید حضوری
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <PrescriptionModal
                open={prescriptionModal.open}
                form={prescriptionForm}
                loading={prescriptionSubmitLoading}
                title={
                    prescriptionModal.mode === "edit" ? "مشاهده و ویرایش نسخه" : "ثبت نسخه جدید"
                }
                submitLabel={prescriptionModal.mode === "edit" ? "ذخیره تغییرات" : "ثبت نسخه"}
                onClose={closePrescriptionModal}
                onChange={setPrescriptionForm}
                onSubmit={handleSubmitPrescription}
            />

            <Dialog open={lensModal.open} onClose={() => closeLensModal()} fullWidth maxWidth="sm">
                <LensFormModalContent close={closeLensModal} />
            </Dialog>

            <QuickGlassModal
                open={quickGlassModal.open}
                loading={quickGlassLoading}
                values={quickGlassForm}
                brands={brands}
                colors={colors}
                categories={initials?.enums?.glassCategory || []}
                onChange={setQuickGlassForm}
                onClose={closeQuickGlassModal}
                onSubmit={handleCreateQuickGlass}
            />
        </div>
    );
}
